import { useCallback } from "react";
import { useEmployeeGate } from "@/hooks/useEmployeeGate";
import { employeeErrorCode, useGetEmployeeMeQuery } from "@/store/api/employeeApi";
import {
  EMP_ERROR_CODES,
  type EmpCapability,
  type EmpMe,
  type EmpRole,
} from "../../../../shared/employee/access";
import {
  NO_BROADCAST_ACCESS,
  type BroadcastLevel,
  type BroadcastPermission,
} from "../../../../shared/employee/broadcastAccess";

export interface EmployeeAccess {
  me: EmpMe | null;
  role: EmpRole | null;
  isLoading: boolean;
  /** Signed in, product on, but no skyEmployee identity (no WLM person record). */
  isNotEmployee: boolean;
  /** Any other failure loading access. */
  isError: boolean;
  can: (cap: EmpCapability) => boolean;
  /** Their level for a broadcast permission ("off" while loading or when absent). */
  broadcast: (permission: BroadcastPermission) => BroadcastLevel;
  /** Messages between colleagues is on (Messaging & broadcasts → Chats). */
  messagesOn: boolean;
}

/**
 * The caller's skyEmployee access, from GET /employee/me. Components ask
 * `can(capability)`, never compare role names. This is display only: the
 * server enforces every capability again.
 */
export function useEmployeeAccess(): EmployeeAccess {
  const gate = useEmployeeGate();
  const { data, error, isLoading } = useGetEmployeeMeQuery(undefined, { skip: !gate.enabled });

  const me = data ?? null;
  const can = useCallback((cap: EmpCapability) => me?.capabilities.includes(cap) ?? false, [me]);
  const broadcast = useCallback(
    (permission: BroadcastPermission) =>
      (me?.broadcast ?? NO_BROADCAST_ACCESS)[permission] ?? "off",
    [me],
  );
  const isNotEmployee = employeeErrorCode(error) === EMP_ERROR_CODES.NOT_AN_EMPLOYEE;

  return {
    me,
    role: me?.role ?? null,
    isLoading: gate.isLoading || (gate.enabled && isLoading),
    isNotEmployee,
    isError: !!error && !isNotEmployee,
    can,
    broadcast,
    /** Messages between colleagues is on (absent from an older server: on). */
    messagesOn: me?.messagesOn !== false,
  };
}
